import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { LogIn, LogOut, Clock, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function ClockTerminal() {
    const { companySlug } = useParams();
    const [pin, setPin] = useState('');
    const [step, setStep] = useState('pin'); // 'pin' | 'action'
    const [checking, setChecking] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [employee, setEmployee] = useState(null);
    const [result, setResult] = useState(null);
    const [time, setTime] = useState(new Date());

    useEffect(() => {
        const t = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const handlePinDigit = (d) => {
        if (pin.length < 4) setPin(p => p + d);
    };

    const handlePinDelete = () => setPin(p => p.slice(0, -1));

    const handlePinSubmit = useCallback(async () => {
        if (pin.length !== 4) return;
        setChecking(true);
        try {
            const res = await axios.post(`${API_BASE}/api/attendance/check/${companySlug}`, { pin });
            setEmployee(res.data);
            setStep('action');
            setResult(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Gecersiz PIN');
            setPin('');
        } finally {
            setChecking(false);
        }
    }, [pin, companySlug]);

    const handleAction = async (action) => {
        setSubmitting(true);
        try {
            const res = await axios.post(`${API_BASE}/api/attendance/clock/${companySlug}/${action}`, { pin });
            setResult(res.data);
            toast.success(action === 'in' ? 'Giris yapildi' : 'Cikis yapildi');
            setTimeout(() => { setStep('pin'); setPin(''); setEmployee(null); setResult(null); }, 4000);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Islem basarisiz');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBack = () => { setStep('pin'); setPin(''); setEmployee(null); setResult(null); };

    return (
        <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4" data-testid="clock-terminal">
            <div className="w-full max-w-sm">
                {/* Time Display */}
                <div className="text-center mb-8">
                    <div className="text-5xl font-light text-white tracking-wider mb-1" data-testid="clock-display">
                        {time.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </div>
                    <div className="text-sm text-gray-500">
                        {time.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </div>
                </div>

                {step === 'pin' && !result && (
                    <div className="space-y-6">
                        {/* PIN dots */}
                        <div className="flex justify-center gap-4 mb-2">
                            {[0,1,2,3].map(i => (
                                <div key={i} className={`w-4 h-4 rounded-full transition-all ${i < pin.length ? 'bg-white scale-110' : 'bg-gray-700'}`} />
                            ))}
                        </div>
                        <p className="text-center text-gray-500 text-sm">PIN kodunuzu girin</p>

                        {/* Numpad */}
                        <div className="grid grid-cols-3 gap-3 max-w-[240px] mx-auto">
                            {[1,2,3,4,5,6,7,8,9].map(d => (
                                <button key={d} onClick={() => handlePinDigit(String(d))} className="aspect-square rounded-2xl bg-gray-800/60 text-white text-xl font-medium hover:bg-gray-700 transition-colors active:scale-95" data-testid={`pin-${d}`}>
                                    {d}
                                </button>
                            ))}
                            <button onClick={handlePinDelete} className="aspect-square rounded-2xl bg-gray-800/30 text-gray-400 text-sm hover:bg-gray-700 transition-colors" data-testid="pin-delete">Sil</button>
                            <button onClick={() => handlePinDigit('0')} className="aspect-square rounded-2xl bg-gray-800/60 text-white text-xl font-medium hover:bg-gray-700 transition-colors" data-testid="pin-0">0</button>
                            <button
                                onClick={handlePinSubmit}
                                disabled={pin.length !== 4 || checking}
                                className="aspect-square rounded-2xl bg-white text-gray-900 text-sm font-semibold hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                                data-testid="pin-submit"
                            >
                                {checking ? <Loader2 size={18} className="animate-spin" /> : 'Gir'}
                            </button>
                        </div>
                    </div>
                )}

                {step === 'action' && !result && employee && (
                    <div className="space-y-6 animate-fade-in">
                        <button onClick={handleBack} className="flex items-center gap-1 text-gray-500 text-sm hover:text-gray-300 transition-colors" data-testid="back-to-pin">
                            <ArrowLeft size={14} /> Geri
                        </button>

                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-3">
                                <span className="text-2xl font-semibold text-white">{employee.employee_name.charAt(0)}</span>
                            </div>
                            <h2 className="text-xl font-medium text-white" data-testid="employee-name">{employee.employee_name}</h2>
                            <p className="text-sm text-gray-400 mt-1" data-testid="employee-status">
                                {employee.is_clocked_in ? `Giris yapilmis (${new Date(employee.clock_in_time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })})` : 'Giris yapilmamis'}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <button
                                onClick={() => handleAction('in')}
                                disabled={employee.is_clocked_in || submitting}
                                className="flex flex-col items-center gap-2 py-6 rounded-2xl transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed bg-green-600/20 text-green-400 hover:bg-green-600/30"
                                data-testid="clock-in-btn"
                            >
                                {submitting ? <Loader2 size={24} className="animate-spin" /> : <LogIn size={24} />}
                                <span className="text-sm font-medium">Giris</span>
                            </button>
                            <button
                                onClick={() => handleAction('out')}
                                disabled={!employee.is_clocked_in || submitting}
                                className="flex flex-col items-center gap-2 py-6 rounded-2xl transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed bg-red-600/20 text-red-400 hover:bg-red-600/30"
                                data-testid="clock-out-btn"
                            >
                                {submitting ? <Loader2 size={24} className="animate-spin" /> : <LogOut size={24} />}
                                <span className="text-sm font-medium">Cikis</span>
                            </button>
                        </div>
                    </div>
                )}

                {result && (
                    <div className="text-center space-y-4 animate-fade-in">
                        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto ${result.action === 'IN' ? 'bg-green-600/20' : 'bg-red-600/20'}`}>
                            {result.action === 'IN' ? <LogIn size={32} className="text-green-400" /> : <LogOut size={32} className="text-red-400" />}
                        </div>
                        <h2 className="text-xl font-medium text-white" data-testid="result-name">{result.employee_name}</h2>
                        <p className={`text-lg font-semibold ${result.action === 'IN' ? 'text-green-400' : 'text-red-400'}`} data-testid="result-action">
                            {result.action === 'IN' ? 'Giris Yapildi' : 'Cikis Yapildi'}
                        </p>
                        {result.duration && <p className="text-gray-400 text-sm">Sure: {result.duration}</p>}
                        {result.is_late && <p className="text-amber-400 text-sm">Gec kalindi</p>}
                        <p className="text-gray-600 text-xs mt-4">Otomatik kapanacak...</p>
                    </div>
                )}

                <div className="text-center mt-8">
                    <div className="flex items-center justify-center gap-2 text-gray-600">
                        <Clock size={14} />
                        <span className="text-xs">Giris/Cikis Terminali</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
